import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

type OldRestaurantDetailsState = {
    name?: string;
    user_name?: string;
    restaurant_name?: string;
    logo?: string;
    logo_black_white?: string;
    logo_encode?: string;
    restaurant_logo?: string;
    isDemoMode?: boolean;
    currency?: string;
    pickup_preparation_time?: number;
    delivery_preparation_time?: number;
    ws_url?: string;
    restaurant_can_delivery?: boolean;
    restaurant_latitude?: number;
    restaurant_longitude?: number;
    restaurant_riderX_address?: AddressData;
    opening_hours?: CashBoxRestaurantOpeningHours[];
    waiter_permissions?: WaiterPermissions;
    ordersListFilters?: CashBoxOrdersListFilters;
}

type RestaurantDetailsContextValue = {
    restaurantDetails: RestaurantDetailsState | undefined;
    setRestaurantDetails: (details: RestaurantDetailsState | undefined) => void;
    clearRestaurantDetails: () => void;
};

const RestaurantDetailsContext = createContext<RestaurantDetailsContextValue | null>(null);

const getRestaurantDetailsFromStorage = (): RestaurantDetailsState | undefined => {
    const restaurantDetailsString = localStorage.getItem("restaurantDetails");

    if (!restaurantDetailsString) return undefined;

    let result: undefined | OldRestaurantDetailsState | RestaurantDetailsState = undefined;
    try {
        const parsedData = JSON.parse(restaurantDetailsString) as OldRestaurantDetailsState | RestaurantDetailsState;

        if (!parsedData) result = {};

        if ("waiter_id" in parsedData && parsedData.waiter_id) {
            result = parsedData;
        }
        else {
            const oldVersionData = parsedData as OldRestaurantDetailsState;

            result = {
                // waiter_id: ,
                waiter_name: oldVersionData.name,
                waiter_email: oldVersionData.user_name,
                restaurant_name: oldVersionData.restaurant_name,
                restaurant_logo: oldVersionData.restaurant_logo || oldVersionData.logo,
                is_demo_mode: oldVersionData.isDemoMode,
                currency: oldVersionData.currency,
                pickup_preparation_time: oldVersionData.pickup_preparation_time,
                delivery_preparation_time: oldVersionData.delivery_preparation_time,
                ws_url: oldVersionData.ws_url,
                restaurant_can_delivery: oldVersionData.restaurant_can_delivery,
                restaurant_latitude: oldVersionData.restaurant_latitude,
                restaurant_longitude: oldVersionData.restaurant_longitude,
                restaurant_riderX_address: oldVersionData.restaurant_riderX_address,
                opening_hours: oldVersionData.opening_hours,
                waiter_permissions: oldVersionData.waiter_permissions,
            } satisfies RestaurantDetailsState;

            delete oldVersionData.isDemoMode;
            delete oldVersionData.logo;
            delete oldVersionData.logo_black_white;
            delete oldVersionData.logo_encode;
            delete oldVersionData.name;
            delete oldVersionData.ordersListFilters;
            delete oldVersionData.user_name;
        }
    } catch (error) {
        console.error("Failed to parse restaurant details", error);
    }

    return result;
};

export const RestaurantDetailsProvider = ({ children }: { children?: ReactNode }) => {
    const [restaurantDetails, setRestaurantDetailsState] = useState<RestaurantDetailsState | undefined>(
        getRestaurantDetailsFromStorage()
    );

    const setRestaurantDetails = useCallback((details: RestaurantDetailsState | undefined) => {
        const nextDetails = details
            ? { ...details }
            : undefined;

        if ((nextDetails as OldRestaurantDetailsState)?.ordersListFilters) delete (nextDetails as OldRestaurantDetailsState).ordersListFilters; // Old versions data

        localStorage.setItem("restaurantDetails", JSON.stringify(nextDetails));
        setRestaurantDetailsState(nextDetails);
    }, []);

    const clearRestaurantDetails = useCallback(() => {
        localStorage.removeItem("restaurantDetails");
        setRestaurantDetailsState(undefined);
    }, []);

    const contextValue = useMemo(() => ({
        restaurantDetails,
        setRestaurantDetails,
        clearRestaurantDetails,
    }), [clearRestaurantDetails, restaurantDetails, setRestaurantDetails]);

    return (
        <RestaurantDetailsContext.Provider
            value={contextValue}
        >
            {children}
        </RestaurantDetailsContext.Provider>
    );
};

export const useRestaurantDetails = (): RestaurantDetailsContextValue => {
    const ctx = useContext(RestaurantDetailsContext);

    if (!ctx) {
        throw new Error(
            "useRestaurantDetails must be used inside RestaurantDetailsProvider"
        );
    }

    return ctx;
};
